import { useTranslation } from "react-i18next";
import { Briefcase } from "iconsax-react";
import { formatDateForDisplay, formatMoneyKzt } from "@/shared/utils";
import type { ServiceItemSummary } from "../../../types";

const SERVICE_COLORS = [
  "background-negative-fill",
  "background-info-fill",
  "background-positive-fill",
  "background-info-fill",
  "background-brand-fill",
  "viz-categorical-01-fill",
  "viz-categorical-02-fill",
  "viz-categorical-03-fill",
  "viz-categorical-04-fill",
  "viz-categorical-05-fill",
  "viz-categorical-06-fill",
];

interface Props {
  items: ServiceItemSummary[];
  locale: "ru" | "kk" | "en";
}

export default function ServiceItemsBlock({ items, locale }: Props) {
  const { t } = useTranslation("EmployeePortal");

  if (items.length === 0) return null;

  return (
    <div className="p-5 radius-3xl surface-base-fill mb-5">
      <h3 className="text-body-bold-lg content-base-primary mb-4">{t("calculations.serviceItems.title")}</h3>
      <div className="grid grid-cols-3 gap-2">
        {items.map(function renderCard(item, index) {
          return (
            <div key={item.id} className="p-5 radius-lg border surface-component-stroke flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div
                  className={`w-8 shrink-0 aspect-square flex justify-center items-center text-white radial-glow surface-component-fill radius-xs ${SERVICE_COLORS[index % SERVICE_COLORS.length]}`}>
                  <Briefcase size={16} color="currentColor" variant="Bold" />
                </div>
                <p className="text-label-sm content-base-primary">{item.serviceName}</p>
              </div>
              <p className="text-body-regular-xs content-action-neutral">
                {formatDateForDisplay(item.startDate)} — {formatDateForDisplay(item.endDate)}
              </p>
              {item.hasPrice && (
                <>
                  <div className="flex gap-0.5">
                    {item.paidPercent > 0 && (
                      <div
                        className="h-1.5 radius-2xs duration-300"
                        style={{ width: `${item.paidPercent}%`, background: "var(--color-brand-500)" }}
                      />
                    )}
                    {item.approvedPercent > 0 && (
                      <div
                        className="h-1.5 radius-2xs duration-300"
                        style={{ width: `${item.approvedPercent}%`, background: "var(--color-orange-500)" }}
                      />
                    )}
                    <div className="h-1.5 radius-2xs surface-component-stroke-fill flex-1" />
                  </div>
                  <div className="flex justify-between">
                    <div className="flex flex-col gap-1">
                      <span className="text-label-xs content-base-secondary">
                        {t("calculations.serviceItems.paid")}
                      </span>
                      <span className="content-base-primary text-body-bold-sm">
                        {formatMoneyKzt(item.paid, locale)}
                      </span>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-label-xs content-base-secondary">
                        {t("calculations.serviceItems.remaining")}
                      </span>
                      <span className="content-base-primary text-body-bold-sm">
                        {formatMoneyKzt(item.total - item.paid - item.approved, locale)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

